import React from 'react';
import { ArrowRight, CheckCircle, AlertCircle } from 'lucide-react';
import { Input, Select } from '../ui';
import useWaitlistForm from '../../hooks/useWaitlistForm';
import { USER_TYPE_OPTIONS } from '../../constants';

// ─── Field wrapper with error message ─────────────────────────────────────────

const FieldGroup = ({ children, error }) => (
  <div className="flex flex-col gap-1">
    {children}
    {error && (
      <span className="text-red-600 text-xs flex items-center gap-1">
        <AlertCircle className="w-3 h-3 flex-shrink-0" />
        {error}
      </span>
    )}
  </div>
);

// ─── Success State ─────────────────────────────────────────────────────────────

const SuccessState = ({ name }) => (
  <div className="text-center py-8 px-4">
    <div className="w-20 h-20 bg-gradient-to-r from-green-400 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg">
      <CheckCircle className="w-10 h-10 text-white" />
    </div>

    <h3 className="text-3xl font-bold text-gray-900 mb-3">
      You're on the waitlist{name ? `, ${name.split(' ')[0]}` : ''}! 🎉
    </h3>

    <p className="text-gray-600 text-lg mb-6 max-w-xl mx-auto leading-relaxed">
      We've added you to the StoveShare waitlist. We're still early in our journey and will keep you updated as we progress.
    </p>

    {/* <div className="bg-white border border-green-200 rounded-xl px-6 py-5 max-w-md mx-auto mb-8 text-left space-y-3">
      <p className="text-green-700 font-semibold text-sm uppercase tracking-wide">What happens next</p>
      <div className="flex items-start gap-3">
        <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
        <p className="text-gray-600 text-sm">You're confirmed on the StoveShare waitlist</p>
      </div>
      <div className="flex items-start gap-3">
        <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
        <p className="text-gray-600 text-sm">We'll reach out as we get closer to launch</p>
      </div>
      <div className="flex items-start gap-3">
        <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
        <p className="text-gray-600 text-sm">Early access and special pricing reserved for you</p>
      </div>
    </div> */}

    <p className="text-gray-400 text-sm">
      Questions? Reach us at{' '}
      <a href="mailto:info@stoveshare.com.au" className="text-red-500 hover:text-red-700">
        info@stoveshare.com.au
      </a>
    </p>
  </div>
);

// ─── Component ────────────────────────────────────────────────────────────────

const WaitlistForm = ({ onSubmit }) => {
  const {
    fields,
    setField,
    setBlurred,
    fieldErrors,
    isSubmitted,
    isLoading,
    submitError,
    handleSubmit,
  } = useWaitlistForm({ onSubmit });

  return (
    <section id="waitlist" className="py-20 bg-gradient-to-br from-red-50 via-red-50 to-red-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="bg-gradient-to-br from-red-50/95 to-red-100/95 backdrop-blur-md border-2 border-red-300/50 rounded-2xl p-6 shadow-2xl relative overflow-hidden">

            {/* Decorative blobs */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-red-300/15 to-transparent rounded-full -translate-y-16 translate-x-16 pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-red-400/15 to-transparent rounded-full translate-y-12 -translate-x-12 pointer-events-none" />

            <div className="relative z-10">

              {/* Show success state after submission */}
              {isSubmitted ? (
                <SuccessState name={fields.name} />
              ) : (
                <>
                  {/* Header */}
                  <div className="text-center mb-6">
                    <h3 className="text-4xl font-bold bg-gradient-to-r from-red-600 to-red-800 bg-clip-text text-transparent mb-3">
                      Join the Revolution
                    </h3>
                    <p className="text-red-700 font-medium text-lg">
                      Get early access and be first to know when we launch! 🚀
                    </p>
                  </div>

                  {/* Fields */}
                  <div className="grid lg:grid-cols-4 gap-4 mb-4">
                    <FieldGroup error={fieldErrors.name}>
                      <Input
                        placeholder="Full Name"
                        value={fields.name}
                        onChange={setField('name')}
                        onBlur={setBlurred('name')}
                        hasError={!!fieldErrors.name}
                      />
                    </FieldGroup>

                    <FieldGroup error={fieldErrors.phone}>
                      <Input
                        type="tel"
                        placeholder="Phone (e.g. 0412 345 678)"
                        value={fields.phone}
                        onChange={setField('phone')}
                        onBlur={setBlurred('phone')}
                        hasError={!!fieldErrors.phone}
                      />
                    </FieldGroup>

                    <FieldGroup error={fieldErrors.email}>
                      <Input
                        type="email"
                        placeholder="Email Address"
                        value={fields.email}
                        onChange={setField('email')}
                        onBlur={setBlurred('email')}
                        hasError={!!fieldErrors.email}
                      />
                    </FieldGroup>

                    <FieldGroup error={fieldErrors.userType}>
                      <Select
                        value={fields.userType}
                        onChange={setField('userType')}
                        onBlur={setBlurred('userType')}
                        options={USER_TYPE_OPTIONS}
                        placeholder="I'm a..."
                        hasError={!!fieldErrors.userType}
                      />
                    </FieldGroup>
                  </div>

                  {/* Interests */}
                  <div className="mb-4">
                    <textarea
                      placeholder="Tell us what you're looking for — kitchen type, location, how often you need it?"
                      value={fields.interests}
                      onChange={setField('interests')}
                      onFocus={(e) => e.target.placeholder = ''}
                      onBlur={(e) => e.target.placeholder = "Tell us what you're looking for — kitchen type, location, how often you need it, or what you plan to cook or produce."}
                      rows={2}
                      className="w-full px-4 py-3 bg-white border-2 border-red-200 rounded-lg focus:ring-2 focus:ring-red-300 focus:border-red-400 text-gray-800 placeholder-gray-400 shadow-md transition-all hover:border-red-300 resize-none outline-none"
                    />
                  </div>

                  {/* Submit error banner */}
                  {submitError && (
                    <div className="flex items-center gap-2 text-red-700 bg-red-100 border border-red-300 rounded-lg px-4 py-3 mb-4 text-sm">
                      <AlertCircle className="w-4 h-4 flex-shrink-0" />
                      {submitError}
                    </div>
                  )}

                  {/* Submit */}
                  <div className="flex justify-center mb-4">
                    <button
                      onClick={handleSubmit}
                      disabled={isLoading}
                      className="bg-gradient-to-r from-red-600 via-red-700 to-red-800 hover:from-red-700 hover:via-red-800 hover:to-red-900 text-white px-8 py-3 rounded-lg font-bold hover:shadow-xl transform hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-2 shadow-lg disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none border border-white/20 min-w-[200px]"
                    >
                      {isLoading ? (
                        <span>Submitting...</span>
                      ) : (
                        <><span>Join Waitlist</span><ArrowRight className="w-4 h-4" /></>
                      )}
                    </button>
                  </div>
                </>
              )}

            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WaitlistForm;